import { AUTH_ME_QUERY_KEY, fetchAuthMeOrAnonymous } from '@/api/hooks/auth';
import type { AuthUser } from '@cotality/marketplace-contracts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Authoritative principal for RBAC gating. Hits the API's `GET /api/auth/me`
 * and exposes the resulting roles + permissions. The same cache entry
 * (`AUTH_ME_QUERY_KEY`) is shared with `useAuthMe`; both hooks resolve
 * through `fetchAuthMeOrAnonymous` so the cache never holds a divergent
 * shape between the two.
 *
 * Pure state read — no side effects. Mid-session re-auth (401 from
 * `/api/auth/me`) is driven by `AuthDriver`, mounted once in `App` and
 * the sole observer that fires `signIn`. Earlier revisions of this hook
 * triggered the sign-in redirect directly from a `useEffect`, which
 * fanned out to every consumer (~20: `RootGate`, `LandingRoute`,
 * `RequirePermissions`, `UserMenu`, `nav-config`, plus per-page hooks)
 * and produced re-entrant redirect errors because nothing coalesced
 * concurrent redirect calls. The structural fix moved the trigger to a
 * single subscriber; this hook stays pure.
 *
 * 403 surfaces as the `'error'` branch. AuthDriver intentionally does
 * NOT trigger on 403 — "authenticated but route locked" (e.g. a failed
 * `user.created` audit) cannot be resolved by re-auth.
 *
 * Public surfaces (`/`, `/auth/login`) consume this hook too. The
 * mutator throws a plain `Error` ("No active account") when there is no
 * active session, so the cache settles into a non-`ApiError` error state
 * and AuthDriver's `instanceof ApiError && status === 401` guard short-
 * circuits.
 *
 * Return shape is a discriminated union — consumers narrow on `status`
 * before touching `user`/`hasPermission`/etc. No compatibility shim:
 * every callsite switches on `status` and handles each branch explicitly.
 */
export type UsePrincipalResult =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | {
      status: 'authenticated';
      user: AuthUser;
      permissions: ReadonlySet<string>;
      hasPermission: (permission: string) => boolean;
      hasAnyPermission: (permissions: readonly string[]) => boolean;
    }
  | { status: 'error'; error: Error };

const EMPTY_PERMS: ReadonlySet<string> = new Set();

/**
 * Wildcard match — `plugins:*` granted at the role layer covers
 * `plugins:approve`, `plugins:reject`, etc. Mirrors the server-side
 * `requirePermissions` check (G3 backend).
 */
function permitsCheck(permissions: ReadonlySet<string>, required: string): boolean {
  if (permissions.has(required)) return true;
  const colon = required.indexOf(':');
  if (colon < 0) return false;
  const wildcard = `${required.slice(0, colon)}:*`;
  return permissions.has(wildcard) || permissions.has('*:*');
}

export function usePrincipal(): UsePrincipalResult {
  const query = useQuery<AuthUser | null>({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: ({ signal }) => fetchAuthMeOrAnonymous(signal),
    staleTime: 5 * 60_000,
    retry: false,
    // Mirror of `useAuthMe`: keep the cache-sharing pair in lockstep. See
    // `api/hooks/auth.ts` for the load-bearing rationale — `retryOnMount:
    // false` is what prevents a refetch storm when multiple observers
    // (RootGate, LandingRoute, AuthedLayout-RequirePermissions, sidebar
    // UserMenu, …) mount against the same errored cache entry.
    retryOnMount: false,
  });

  const user = query.data ?? null;

  const permissionsResult = useMemo(() => {
    if (user === null) return null;
    const set: ReadonlySet<string> = new Set(user.permissions);
    return {
      permissions: set,
      hasPermission: (perm: string) => permitsCheck(set, perm),
      hasAnyPermission: (perms: readonly string[]) => perms.some((p) => permitsCheck(set, p)),
    };
  }, [user]);

  if (query.isLoading) {
    return { status: 'loading' };
  }

  if (query.isError) {
    // 401 surfaces here as `'error'` AND `AuthDriver` fires the re-auth
    // redirect from a single subscriber. Callers that need to render a
    // "sign in" affordance for both anonymous and error principals
    // (UserMenu, Login) treat both branches the same; callers that want
    // stricter semantics (RootGate, RequirePermissions) fail-closed on
    // `'error'`.
    const err =
      query.error instanceof Error ? query.error : new Error(String(query.error ?? 'Unknown'));
    return { status: 'error', error: err };
  }

  if (user === null || permissionsResult === null) {
    // `fetchAuthMeOrAnonymous` resolves to `null` for the public-landing
    // (no active session) case — explicitly anonymous, not a query error.
    return { status: 'anonymous' };
  }

  return {
    status: 'authenticated',
    user,
    permissions: permissionsResult.permissions,
    hasPermission: permissionsResult.hasPermission,
    hasAnyPermission: permissionsResult.hasAnyPermission,
  };
}

// Internal escape hatch: components/hooks that need to ask "does the
// principal hold this perm OR are we loading/anonymous?" can use this
// helper to avoid an `if (status !== 'authenticated') return false` ladder
// at every callsite. Defaults to `false` for non-authenticated states —
// matches the fail-closed posture every consumer wants.
export function hasPermissionOrFalse(principal: UsePrincipalResult, permission: string): boolean {
  return principal.status === 'authenticated' ? principal.hasPermission(permission) : false;
}

export function hasAnyPermissionOrFalse(
  principal: UsePrincipalResult,
  permissions: readonly string[],
): boolean {
  return principal.status === 'authenticated' ? principal.hasAnyPermission(permissions) : false;
}

// Used internally; re-export so test files that need to construct the
// empty perms set for stubs can reach it.
export { EMPTY_PERMS };
